import { useState } from 'react';
import {
  closestCorners,
  DndContext,
  DragOverlay,
  pointerWithin,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
  type SensorDescriptor,
  type SensorOptions,
} from '@dnd-kit/core';
import type { BoardDto, CardDto } from '../../lib/types';
import { StreamColumn } from './StreamColumn';
import { CardTileOverlay, type SetMonth } from './CardTile';
import { HighlightContext } from './highlight';

/**
 * Pointer drags: prefer the card under the pointer (insert at its index), then the section zone
 * under the pointer (append); nothing under the pointer means no target, so the drag cancels.
 * Keyboard drags have no pointer coordinates, so they use closest corners.
 */
const collisionDetection: CollisionDetection = (args) => {
  if (!args.pointerCoordinates) return closestCorners(args);
  const hits = pointerWithin(args);
  const cardHit = hits.find((h) => typeof h.id === 'number');
  return cardHit ? [cardHit] : hits;
};

interface BoardViewProps {
  board: BoardDto;
  canWrite: boolean;
  /** Drag sensors; omitted means no sensors (read-only, nothing can be dragged). */
  sensors?: SensorDescriptor<SensorOptions>[];
  onOpenCard: (cardId: number) => void;
  onEditStream: (streamId: number) => void;
  onAdjustWorkers: (streamId: number, delta: 1 | -1) => void;
  /** Write users: set a card's month from its tile pill. Omitted on read-only views. */
  onSetMonth?: SetMonth;
  onDragStart?: (e: DragStartEvent) => void;
  onDragEnd?: (e: DragEndEvent) => void;
  onDragCancel?: () => void;
  /** The card being dragged, rendered in the drag overlay. */
  activeCard?: CardDto | null;
}

/**
 * The stream columns of a board inside the drag context and the dependency-highlight context.
 * Used by the live Work page and, read-only, by the commit snapshot page.
 */
export function BoardView({
  board, canWrite, sensors, onOpenCard, onEditStream, onAdjustWorkers, onSetMonth,
  onDragStart, onDragEnd, onDragCancel, activeCard = null,
}: BoardViewProps) {
  // Card to glow while a "depends on" chip is hovered somewhere on the board.
  const [highlightedCardId, setHighlightedCardId] = useState<number | null>(null);
  const noSensors = useSensors();

  return (
    <HighlightContext.Provider value={{ highlightedCardId, setHighlightedCardId }}>
      <DndContext
        sensors={sensors ?? noSensors}
        collisionDetection={collisionDetection}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        {/* Up to 7 columns share the width; past that each keeps the 7-column width and the board scrolls sideways. */}
        <div className="flex gap-3 overflow-x-auto pb-2">
          {board.streams.map((stream) => (
            <StreamColumn
              key={stream.id}
              stream={stream}
              canWrite={canWrite}
              onOpenCard={onOpenCard}
              onEditStream={onEditStream}
              onAdjustWorkers={onAdjustWorkers}
              onSetMonth={onSetMonth}
            />
          ))}
        </div>
        {/* No drop animation: the overlay would tween back toward the card's old slot before the move renders. */}
        <DragOverlay dropAnimation={null}>{activeCard ? <CardTileOverlay card={activeCard} /> : null}</DragOverlay>
      </DndContext>
    </HighlightContext.Provider>
  );
}

/** Department-wide counter: the sum of every stream's "N of M used". */
export function TotalDevsPill({ board }: { board: BoardDto }) {
  const totalUsed = board.streams.reduce((n, s) => n + s.usedWorkers, 0);
  const totalWorkers = board.streams.reduce((n, s) => n + s.workerCount, 0);
  return (
    <span
      className={`text-sm font-semibold rounded-full px-3 py-1 ${
        totalUsed > totalWorkers ? 'bg-red-600 text-white' : 'bg-atg-accent text-atg-navy'
      }`}
      aria-live="polite"
    >
      Total devs: {totalUsed} used of {totalWorkers}
    </span>
  );
}
